import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { useProjectMembersStore } from '@/stores/projectMembersStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useTabsStore, MAX_OPEN_TABS } from '@/stores/tabsStore'
import { useTabLimitStore } from '@/stores/tabLimitStore'
import { useModalStore } from '@/hooks/useModalStore'
import { projectCan } from '@/hooks/useProjectCan'
import { useConfirmStore } from '@/hooks/useConfirmStore'
import { usePaginatedList } from '@/hooks/usePaginatedList'
import { projectApi, type ProjectListParams } from '@/api/projectApi'
import { NewProjectModal } from '@/components/Modals/NewProjectModal'
import { SqlImportModal } from '@/components/Modals/SqlImportModal'
import { EditProjectModal } from '@/components/Modals/EditProjectModal'
import { ManageAccessDialog } from '@/components/projects/ManageAccessDialog'
import { TransferProjectDialog } from '@/components/workspace/TransferProjectDialog'
import { ProjectListCard } from '@/components/projects/projectlistcard'
import { SkeletonCard, SkeletonInput } from '@/components/ui/Skeleton'
import { EmptyState as UiEmptyState } from '@/components/ui/EmptyState'
import { SiteShell } from '@/components/layout/SiteShell'
import { PlanChip } from '@/components/billing/PlanChip'
import { Pagination } from '@/components/ui/Pagination'
import { DropdownMenu } from '@/components/ui/DropdownMenu'
import { BackIcon, NewProjectIcon, SettingsIcon, TableIcon } from '@/components/ui/icons'
import { WorkspaceRoleValue, type ProjectListItem, type Workspace } from '@/api/types'

const PAGE_SIZE = 20
const DEFAULT_FILTERS: ProjectListParams = { expand: 'current_commit' }

function ActionButton({
  children,
  onClick,
  primary,
  danger,
}: {
  children: ReactNode
  onClick: () => void
  primary?: boolean
  danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: primary ? '#3b82f6' : 'transparent',
        border: `1px solid ${danger ? 'var(--color-danger-border)' : primary ? '#3b82f6' : 'var(--border)'}`,
        color: danger ? 'var(--color-danger)' : primary ? 'white' : 'var(--text)',
        borderRadius: 8,
        padding: '8px 18px',
        fontSize: 14,
        fontWeight: primary ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

function ProjectsEmpty({
  onNew,
  onImportSql,
}: {
  onNew: () => void
  onImportSql: () => void
}) {
  const { t } = useTranslation()
  return (
    <UiEmptyState
      icon="📁"
      title={t('projects.empty.title', 'Nenhum projeto ainda')}
      description={t(
        'projects.empty.subtitle',
        'Crie um novo projeto ou importe um existente para começar.',
      )}
      actions={
        <>
          <ActionButton onClick={onNew} primary>
            {t('projects.empty.createNew', 'Criar novo')}
          </ActionButton>
          <ActionButton onClick={onImportSql}>
            {t('projects.empty.importSql', 'Importar SQL')}
          </ActionButton>
        </>
      }
    />
  )
}

export default function ProjectListPage() {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const memberships = useAuthStore((s) => s.memberships)
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const subscription = useWorkspaceStore((s) => s.subscription)
  const loadWorkspaceBySlug = useWorkspaceStore((s) => s.loadWorkspaceBySlug)
  const loadWorkspaces = useWorkspaceStore((s) => s.loadWorkspaces)
  const loadSubscription = useWorkspaceStore((s) => s.loadSubscription)
  const { softDelete } = useProjectsStore()
  const { openNewProject, openSqlImport } = useModalStore()
  const showConfirm = useConfirmStore((s) => s.show)
  const tabs = useTabsStore((s) => s.tabs)
  const showTabLimit = useTabLimitStore((s) => s.show)
  const openProjectIds = useMemo(
    () => new Set(tabs.map((t) => t.projectId).filter(Boolean)),
    [tabs],
  )
  const wsRole = useWorkspaceStore((s) => s.myRole)
  const [editingProject, setEditingProject] = useState<ProjectListItem | null>(null)
  const [transferProject, setTransferProject] = useState<ProjectListItem | null>(null)
  const [managingAccessProject, setManagingAccessProject] = useState<ProjectListItem | null>(null)

  const workspaceId = currentWorkspace?.id
  const isReady = !!workspaceId && currentWorkspace?.slug === workspaceSlug

  const projectMembers = useProjectMembersStore((s) => s.members)

  const { items, total, loading, page, totalPages, filters, setFilter, setPage, reload, patchItems } =
    usePaginatedList<ProjectListItem, ProjectListParams>(
      (params) => projectApi.list(workspaceId!, params),
      {
        initialLimit: PAGE_SIZE,
        initialFilters: DEFAULT_FILTERS,
        enabled: isReady,
      },
    )

  useEffect(() => {
    if (!memberships.length) return
    void loadWorkspaces(memberships)
  }, [memberships, loadWorkspaces])

  useEffect(() => {
    if (!workspaceSlug) return
    if (currentWorkspace?.slug === workspaceSlug) return
    void loadWorkspaceBySlug(workspaceSlug).catch(() => {
      navigate('/workspaces', { replace: true })
    })
  }, [workspaceSlug, currentWorkspace?.slug, loadWorkspaceBySlug, navigate])

  useEffect(() => {
    if (!isReady || !workspaceId) return
    void loadSubscription(workspaceId)
  }, [isReady, loadSubscription, workspaceId])

  function handleOpen(project: ProjectListItem) {
    const url = `/workspace/${workspaceSlug}/projects/${project.id}/editor`
    const alreadyOpen = tabs.some((tab) => tab.projectId === String(project.id))
    if (!alreadyOpen && tabs.length >= MAX_OPEN_TABS) {
      showTabLimit(url)
      return
    }
    navigate(url)
  }

  function handleDelete(id: number) {
    showConfirm(
      t('projects.deleteTitle', 'Deletar projeto'),
      t('projects.deleteConfirm', 'Excluir este projeto? Esta ação não pode ser desfeita.'),
      () => {
        void softDelete(id).then(reload)
      },
    )
  }

  function handleManageAccessClose() {
    if (managingAccessProject) {
      const preview = projectMembers.slice(0, 5).map((m) => ({
        id: m.id,
        role: m.role,
        name: m.user.name || m.user.email,
        email: m.user.email,
      }))
      patchItems((current) =>
        current.map((p) =>
          p.id === managingAccessProject.id
            ? { ...p, member_preview: preview, member_count: projectMembers.length }
            : p,
        ),
      )
    }
    setManagingAccessProject(null)
  }

  function handleTransferSuccess(project: ProjectListItem, targetWorkspace: Workspace) {
    reload()
    const isOpen = tabs.some((tab) => tab.projectId === String(project.id))
    if (isOpen) {
      navigate(`/workspace/${targetWorkspace.slug}/projects/${project.id}/editor`)
    }
  }

  const searchValue = (filters.name as string) ?? ''

  return (
    <SiteShell>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          background: 'var(--bg-app)',
          color: 'var(--text)',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 32px 16px',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={() => navigate('/workspaces')}
              aria-label={t('common.back', 'Voltar')}
              title={t('common.back', 'Voltar')}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                borderRadius: 8,
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BackIcon />
            </button>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <h1 style={{ margin: 0, fontSize: 22 }}>
                  {currentWorkspace?.name ?? t('projects.title', 'Projetos')}
                </h1>
                <PlanChip
                  code={subscription?.plan.code ?? currentWorkspace?.subscription_summary?.plan_code ?? 'free'}
                  prominent
                />
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.5 }}>
                {t('projects.subtitle', 'Selecione ou crie um projeto')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <DropdownMenu
              label={t('projects.createNew', 'Criar novo')}
              icon={<NewProjectIcon />}
              sections={[
                {
                  id: 'create-project-actions',
                  items: [
                    {
                      id: 'create-new',
                      icon: <NewProjectIcon />,
                      title: t('projects.createNew', 'Criar novo'),
                      description: t(
                        'projects.createNewDescription',
                        'Criar um projeto vazio neste workspace',
                      ),
                      onSelect: openNewProject,
                    },
                    {
                      id: 'import-sql',
                      icon: <TableIcon />,
                      title: t('projects.importSql', 'Importar SQL'),
                      description: t(
                        'projects.importSqlDescription',
                        'Importar estrutura a partir de um arquivo SQL',
                      ),
                      onSelect: () => openSqlImport('project-list'),
                    },
                  ],
                },
              ]}
            />
            <button
              onClick={() => navigate(`/workspace/${workspaceSlug}/manager`)}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                borderRadius: 8,
                height: 30,
                padding: '0 14px',
                fontSize: 13,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <SettingsIcon />
              {t('workspace.manage', 'Gerenciar workspace')}
            </button>
          </div>
        </div>

        {/* Search — pinned, always visible */}
        <div style={{ padding: '16px 32px 0', flexShrink: 0 }}>
          {loading && total === 0 ? (
            <SkeletonInput maxWidth={320} />
          ) : (
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setFilter('name', e.target.value)}
              placeholder={t('projects.search', 'Buscar projeto...')}
              className="morph-input"
              style={{ maxWidth: 320 }}
            />
          )}
        </div>

        {/* Grid — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 32px' }}>
          {loading ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 14,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} row height={80} />
              ))}
            </div>
          ) : total === 0 && !searchValue ? (
            <ProjectsEmpty
              onNew={openNewProject}
              onImportSql={() => openSqlImport('project-list')}
            />
          ) : items.length === 0 ? (
            <div
              style={{
                padding: '24px 0',
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--text-dim)',
              }}
            >
              {t('projects.noMatch', 'Nenhum projeto corresponde à busca.')}
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 14,
              }}
            >
              {items.map((project) => {
                const canDelete = projectCan('delete', project.my_project_role ?? null, wsRole)
                const canEdit = projectCan('rename', project.my_project_role ?? null, wsRole)
                const canMove = wsRole === WorkspaceRoleValue.OWNER
                const canManageAccess = projectCan(
                  'manage_members',
                  project.my_project_role ?? null,
                  wsRole,
                )

                return (
                <ProjectListCard
                  key={project.id}
                  project={project}
                  canDelete={canDelete}
                  canEdit={canEdit}
                  canMove={canMove}
                  canManageAccess={canManageAccess}
                  onOpen={handleOpen}
                  onDelete={handleDelete}
                  onEdit={setEditingProject}
                  onMove={setTransferProject}
                  onManageAccess={setManagingAccessProject}
                  isOpen={openProjectIds.has(String(project.id))}
                />
                )
              })}
            </div>
          )}
        </div>

        {/* Paginator — pinned at bottom, outside scroll */}
        <div style={{ flexShrink: 0, padding: '0 32px 16px' }}>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>
      <NewProjectModal workspaceId={workspaceId ?? 0} />
      <EditProjectModal
        project={editingProject}
        onClose={() => {
          setEditingProject(null)
          reload()
        }}
      />
      <SqlImportModal workspaceId={workspaceId ?? 0} />
      <TransferProjectDialog
        open={transferProject !== null}
        onClose={() => setTransferProject(null)}
        project={transferProject}
        currentWorkspaceId={workspaceId ?? 0}
        onSuccess={(targetWs) => {
          if (transferProject) handleTransferSuccess(transferProject, targetWs)
          setTransferProject(null)
        }}
      />
      {managingAccessProject && (
        <ManageAccessDialog
          open={managingAccessProject !== null}
          onClose={handleManageAccessClose}
          projectId={managingAccessProject.id}
          projectName={managingAccessProject.name}
          workspaceId={workspaceId ?? 0}
        />
      )}
    </SiteShell>
  )
}
